import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import { cx } from "@ui/utils/cx";
import { useEffect, useId, useRef, useState } from "react";

export type SectionTitleVariant = "left" | "right" | "mobile";

// Exact reference ink colour (#d9d7cf) — a warm light grey that reads as printed,
// not as a placeholder.
const INK = "#d9d7cf";

// Desktop: one SVG overlays the whole book. Its viewBox matches the book's
// 1426:1000 aspect, so every coordinate is a book-percentage and maps 1:1 onto
// the rendered book regardless of size.
const BOOK_VIEWBOX = "0 0 1426 1000";
const BOOK_FONT = 52; // cap height ≈ 2.55% of book width — slightly smaller than the
// original 60 to leave headroom for the "(continued)" note on a section's later pages.
// Letter spacing is correspondingly tightened (see LETTER_SPACING).
// The title is *printed onto the curved page*: its baseline is the page's own
// top-edge curve (traced from passport-desktop.webp — a concave-down arc that
// peaks mid-page and dips toward the outer corner and the spine) offset straight
// down into the top margin, so the text runs parallel to the page and bends with
// it instead of sitting flat on a slant. It is centred both ways over the badge
// grid it labels: horizontally on the grid's centre (x≈403, the midpoint of the
// two badge columns) and vertically in the gap between the page top and the first
// badge row. The right page mirrors the left about the book centre (x=713): the
// left descends into the gutter, the right rises out of it. Baselines are raised
// ~12 units vs the original so the title + its "(continued)" note both clear the
// first badge row.
const BOOK_PATH: Record<"left" | "right", string> = {
  left: "M 154 101.24 Q 403 84.68 652 111.52",
  right: "M 774 111.52 Q 1023 84.68 1272 101.24",
};
// Widest a title may render before it would cross the spine or the outer page
// edge. Longer localized labels (e.g. ES "CELEBRACIONES", DE "GEMEINSCHAFT")
// shrink to fit this width rather than clipping.
const BOOK_MAX_TEXT_WIDTH = 470;

// The small "(continued)" note printed under a title on a section's later pages.
// It rides a curve PARALLEL to the title — BOOK_PATH shifted straight down 28 units —
// so it tucks just beneath the title at the same angle and bend, not flat, leaving a
// small (~6px rendered) gap. Desktop only. (Keep in sync with BOOK_PATH if retraced.)
const BOOK_CONTINUED_PATH: Record<"left" | "right", string> = {
  left: "M 154 129.24 Q 403 112.68 652 139.52",
  right: "M 774 139.52 Q 1023 112.68 1272 129.24",
};
const BOOK_CONTINUED_FONT = 22;

// Mobile: a flat title sized to the badge column. The sheet is flat, so the line
// is straight.
const MOBILE_VIEWBOX = "0 0 400 74";
const MOBILE_FONT = 48;
const MOBILE_PATH = "M 8 50 L 392 50";
const MOBILE_MAX_TEXT_WIDTH = 384;

// Tighter than the original 4 — pairs with the reduced font size for a more compact
// label. Shared by both surfaces and the hidden measurer.
const LETTER_SPACING = "3";

interface CurvedSectionTitleProps {
  section: Badge["section"];
  variant: SectionTitleVariant;
  /**
   * Marks a title that re-states a section already begun on an earlier page (a
   * section spanning multiple pages). Prints a small "(continued)" note under the
   * title. Desktop book only — the mobile notepad titles each tile once and never
   * sets this.
   */
  continued?: boolean;
  className?: string;
}

/**
 * Passport section heading. Renders the localized section name as SVG text
 * printed along the page (curved into the gutter on the desktop book, flat on the
 * mobile notepad — see geometry constants). Long localized labels shrink to fit
 * the page width. Decorative (aria-hidden) and non-interactive so it never
 * intercepts badge hovers — the name is exposed to assistive tech via the
 * adjacent visually-hidden heading.
 */
export function CurvedSectionTitle({ section, variant, continued = false, className }: CurvedSectionTitleProps) {
  // Unique per instance so two titles in one spread (or many on the notepad)
  // don't share a path id — a shared id would make every textPath follow the
  // first path.
  const pathId = useId();
  const continuedPathId = useId();
  // Dynamic key — declare the resolved keys so i18n-tasks sees them as used.
  // i18n-tasks-use t('passport.badges.sections.community')
  // i18n-tasks-use t('passport.badges.sections.trips')
  // i18n-tasks-use t('passport.badges.sections.countries')
  // i18n-tasks-use t('passport.badges.sections.continents')
  // i18n-tasks-use t('passport.badges.sections.marketplace')
  // i18n-tasks-use t('passport.badges.sections.celebrations')
  // `section` is typed as a known key, but a badge can reach the client with an
  // absent/unmapped section (the column is nullable and backfilled async), so
  // resolve defensively — an empty label renders nothing rather than a literal
  // missing-translation string.
  const label = section ? t(`passport.badges.sections.${section}`).toUpperCase() : "";
  // Lower-case note kept distinct from the upper-cased title so it reads as a
  // quiet annotation, not a second heading.
  const continuedLabel = t("passport.badges.sections.continued");

  const mobile = variant === "mobile";
  const viewBox = mobile ? MOBILE_VIEWBOX : BOOK_VIEWBOX;
  const baseFont = mobile ? MOBILE_FONT : BOOK_FONT;
  const maxTextWidth = mobile ? MOBILE_MAX_TEXT_WIDTH : BOOK_MAX_TEXT_WIDTH;
  const d = mobile ? MOBILE_PATH : BOOK_PATH[variant];

  // Shrink the font for any localized label too wide for the page/sheet, so the
  // text never overruns the spine or the column. Measured from a hidden copy
  // rendered at the base size; re-measured once webfonts finish loading because
  // glyph widths differ from the fallback face.
  const measureRef = useRef<SVGTextElement>(null);
  const [fontSize, setFontSize] = useState(baseFont);
  useEffect(() => {
    const node = measureRef.current;
    if (!node) return;
    let cancelled = false;
    const measure = () => {
      // The fonts.ready callback can resolve after this title pages away
      // (AnimatePresence unmounts it); guard against measuring a detached node
      // or setting state on an unmounted component.
      if (cancelled || !node.isConnected) return;
      node.textContent = label;
      const width = node.getComputedTextLength();
      setFontSize(width > maxTextWidth ? (baseFont * maxTextWidth) / width : baseFont);
    };
    measure();
    // Webfonts load async; re-measure once they settle (glyph widths differ from
    // the fallback face, which would otherwise mis-size long labels).
    if (document.fonts?.ready) void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [label, baseFont, maxTextWidth]);

  // Nothing to print for an absent/unknown section — skip the heading entirely
  // rather than rendering a broken title.
  if (!label) return null;

  return (
    <div className={cx("pointer-events-none select-none", className)}>
      {/* Note the continuation in the heading too, so a screen-reader user paging
          through a multi-page section hears "Countries (continued)" rather than a
          bare repeat of the same heading. */}
      <h3 className="sr-only">{continued && continuedLabel ? `${label} ${continuedLabel}` : label}</h3>
      <svg aria-hidden="true" viewBox={viewBox} className="w-full overflow-visible" xmlns="http://www.w3.org/2000/svg">
        <path id={pathId} d={d} fill="none" />
        {/* Hidden measurer at the base size; the effect sets its text and reads
            getComputedTextLength to derive the shrink-to-fit scale. */}
        <text
          ref={measureRef}
          className="font-passport font-semibold"
          fontSize={baseFont}
          letterSpacing={LETTER_SPACING}
          visibility="hidden"
        />
        <text
          fill={INK}
          className="font-passport font-semibold"
          fontSize={fontSize}
          letterSpacing={LETTER_SPACING}
          textAnchor="middle"
        >
          <textPath href={`#${pathId}`} startOffset="50%">
            {label}
          </textPath>
        </text>
        {/* "(continued)" note tucked just under the title on a section's later pages,
            riding a curve parallel to the title (desktop book only — the mobile
            notepad never sets `continued`). */}
        {!mobile && continued && continuedLabel && (
          <>
            <path id={continuedPathId} d={BOOK_CONTINUED_PATH[variant === "right" ? "right" : "left"]} fill="none" />
            <text
              fill={INK}
              className="font-medium font-passport"
              fontSize={BOOK_CONTINUED_FONT}
              letterSpacing="1.5"
              textAnchor="middle"
            >
              <textPath href={`#${continuedPathId}`} startOffset="50%">
                {continuedLabel}
              </textPath>
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
